/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, {
  attr,
  belongsTo,
  type AsyncBelongsTo,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import type { Type } from '@warp-drive/core/types/symbols';
import type Person from '@linc-technologies/linced-common/models/person';
import type School from '@linc-technologies/linced-common/models/school';

// Proto: attendance.NzMoeDay.
export default class NzMoeDay extends Model {
  declare [Type]: 'attendance/nz-moe-day';

  // schoolId is the school the attendance record is assigned to.
  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  // campusId is the school campus the attendance record is assigned to.
  @attr('string', { defaultValue: '' })
  declare campusId: string;

  // createTime is when the resource was created in seconds from the epoch.
  @attr('number')
  declare createTime: number | undefined;

  // updateTime is the last time the resource was modified in seconds from
  // the epoch.
  @attr('number')
  declare updateTime: number | undefined;

  // personId  the unique identifier of the person who this attendance record
  // is for.
  @belongsTo('person', { async: true, inverse: null })
  declare personId: AsyncBelongsTo<Person>;

  @alias('personId')
  declare person: AsyncBelongsTo<Person>;

  @alias('person.id')
  declare person_id: string | null | undefined;

  // dateId is the date the attendance is assigned to.
  @belongsTo('date', { async: true, inverse: null })
  declare dateId: AsyncBelongsTo<Model>;

  @alias('dateId')
  declare dateObj: AsyncBelongsTo<Model>;

  @alias('dateObj.id')
  declare date_id: string | null | undefined;

  // date contains the date the attendance was recorded against as a string
  // to facilitate filtering.
  @attr('string')
  declare date: string | undefined;

  // present provides a count of "half-days attended", per the MoE half-day
  // calculation.
  @attr('number', { defaultValue: 0 })
  declare present: number;

  // unjustifiablyAbsent provides a count of half-day unjustifiable absences,
  // per the MoE half-day unjustified absence calculation.
  // This is not actually a count of half-days.
  @attr('number', { defaultValue: 0 })
  declare unjustifiablyAbsent: number;

  @attr('number', { defaultValue: 0 })
  declare examLeave: number;

  // exempt provides a count of "half-days exempt", per the MoE half-day
  // calculation.
  @attr('number', { defaultValue: 0 })
  declare exempt: number;

  // code provides the NZMoE overall attendance code for the whole day based
  // on the individual attendance records.
  @attr('string', { defaultValue: '' })
  declare code: string;

  // exemptCode captures the exempt code that was used for days when we have half absence & exempt (H or M)
  @attr('string', { defaultValue: '' })
  declare exemptCode: string;

  @attr('boolean', { defaultValue: false })
  declare isLate: boolean;

  // hasMinorPartialAbsent is set to true if a student was partially unjustifiably absent for less than 120 minutes for the whole day.
  @attr('boolean', { defaultValue: false })
  declare hasMinorPartialAbsence: boolean;

  // singleHalfDay provides a boolean which tells us when we have days when only 1 half-day code is present
  @attr('boolean', { defaultValue: false })
  declare singleHalfDay: boolean;

  @computed(
    'present',
    'unjustifiablyAbsent',
    'exempt',
    'examLeave',
    'code',
    'singleHalfDay',
  )
  get justifiedAbsent() {
    if (this.code === 'M' || this.code === 'H') {
      return 0;
    }
    if (this.singleHalfDay) {
      if (this.code === 'J') {
        return 1;
      }
      return 0;
    }
    return (
      2 -
      (this.unjustifiablyAbsent + this.present + this.examLeave + this.exempt)
    );
  }

  @computed('present')
  get presentWholeDays() {
    return this.present / 2;
  }

  @computed('unjustifiablyAbsent')
  get unjustifiablyAbsentWholeDays() {
    return this.unjustifiablyAbsent / 2;
  }

  @computed('justifiedAbsent')
  get justifiedAbsentWholeDays() {
    return this.justifiedAbsent / 2;
  }

  @computed('code')
  get status() {
    switch (this.code) {
      case 'P':
      case 'I':
        return 'present';
      case 'J':
        return 'justified';
      case 'U':
        return 'unjustified';
      case 'O':
        return 'overseas';
    }
  }
}
